import posthog from 'posthog-js';

let initialized = false;

/**
 * Initialize PostHog. Call once at app startup, before React renders.
 *
 * No-op when REACT_APP_POSTHOG_KEY is not set — useful for local dev so
 * developers don't accidentally ship test traffic to the prod project.
 *
 * `consentGranted` decides whether capturing starts immediately. It is passed in
 * rather than read from src/utils/consent.ts so that this module stays a leaf:
 * consent.ts calls setPostHogConsent() below, and a cycle between the two would
 * leave one of them half-initialised at import time.
 */
export const initPostHog = (consentGranted: boolean): void => {
  if (initialized) return;

  const key = process.env.REACT_APP_POSTHOG_KEY;
  const host = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';

  if (!key) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.info(
        '[posthog] REACT_APP_POSTHOG_KEY is not set — analytics disabled.'
      );
    }
    return;
  }

  posthog.init(key, {
    api_host: host,

    // Nothing is captured — not events, not session replay — until the visitor
    // accepts. The site markets in EU languages under /de, /fr, /es and /pt-BR,
    // and replaying an EU visitor's session before they have agreed to it is not
    // something a cookie banner on the Google tags alone would make lawful.
    // ConsentBanner flips this via setPostHogConsent() the moment they choose.
    opt_out_capturing_by_default: !consentGranted,

    // Capture every click, pageview, form submit, and input change automatically.
    // This is what gives us the "every page visited, every mouse click" data
    // without having to manually instrument every component.
    autocapture: true,
    capture_pageview: true,
    capture_pageleave: true,

    // Session replay — record what the user sees and does.
    // mask: true on inputs/text by default would redact everything; we keep
    // text visible but mask any element marked with `.ph-no-capture` or
    // with attribute data-ph-mask. Explicitly mask password and api-key inputs.
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: {
        password: true,
      },
      // Block elements marked by the app as sensitive (e.g. API key displays).
      // Add the `ph-no-capture` class or `data-ph-mask` attribute to any DOM
      // node whose contents must not appear in replays.
      maskTextSelector: '[data-ph-mask]',
    },

    // Only create a Person profile when we explicitly call identify() on login.
    // Anonymous visitors are still tracked with a distinct_id and their
    // sessions are still recorded and merged into the Person on signup.
    person_profiles: 'identified_only',

    // Don't spam Cognito redirect URLs / OAuth fragments into event props.
    sanitize_properties: (properties: Record<string, unknown>) => {
      const url = properties['$current_url'];
      if (typeof url === 'string') {
        try {
          const parsed = new URL(url);
          // Drop the hash — Cognito puts id_token / access_token in there.
          parsed.hash = '';
          properties['$current_url'] = parsed.toString();
        } catch {
          // leave as-is if unparseable
        }
      }
      return properties;
    },

    loaded: (ph) => {
      if (process.env.REACT_APP_DEBUG === 'true') {
        ph.debug();
      }
    },
  });

  initialized = true;
};

/**
 * Starts or stops capturing in response to the visitor's consent choice.
 *
 * Safe to call when PostHog was never initialised (no REACT_APP_POSTHOG_KEY): the
 * stub swallows it. Wrapped anyway, because a consent banner that throws out of
 * its click handler is worse than one that fails to enable analytics.
 */
export const setPostHogConsent = (granted: boolean): void => {
  try {
    if (granted) {
      posthog.opt_in_capturing();
    } else {
      posthog.opt_out_capturing();
    }
  } catch {
    // PostHog not loaded — nothing to opt in or out of.
  }
};

export { posthog };
